// src/types/issue.types.ts — SCE Issues
// Types centraux du domaine métier
//
// ─── Lot 3.5 — Simplification du formulaire de déclaration ───
//
// La Famille et la Criticité ne sont plus saisies à la création par le déclarant :
// elles sont initialisées à la valeur sentinelle 'À qualifier' (Option β),
// puis renseignées par le coordinateur métier depuis la fiche détail.
//
// Conséquences sur les types :
//  - Criticite est élargi à 'À qualifier'
//  - famille reste typé `string` (libre — déjà le cas) mais peut contenir 'À qualifier'
//  - NewIssuePayload omet désormais famille + criticite (l'API les force à 'À qualifier')

// ─── Énumérations métier ───

export type Metier =
  | 'ELEC'
  | 'AUTOM'
  | 'MECA'
  | 'PROCESS'

/**
 * Sujet (23/10) : un problème d'UN métier, celui qui corrige, qui concerne
 * aussi quelqu'un d'ailleurs. Goulotte : toujours la Méca, Thibaut informé et
 * pilote. Colonne texte `Sujet` ; règles et responsables : config/sujets.config.
 */
export type Sujet = 'Goulotte'
export const SUJETS: readonly Sujet[] = ['Goulotte']

export function estSujet(valeur: unknown): valeur is Sujet {
  return typeof valeur === 'string' && (SUJETS as readonly string[]).includes(valeur)
}

/**
 * Criticité d'un problème.
 *
 * 'À qualifier' = valeur sentinelle posée à la création par le déclarant ;
 *                 le coordinateur métier la remplace par Critique/Majeur/Mineur
 *                 depuis la fiche détail.
 *
 * ⚠️ Côté SharePoint (SCE_Problemes / field_4), la liste de choix doit inclure
 *    exactement 'À qualifier' (avec À majuscule accentué) — sinon écriture rejetée.
 */
export type Criticite = 'Critique' | 'Majeur' | 'Mineur' | 'À qualifier'

/**
 * Constante magique pour la valeur sentinelle.
 * À utiliser plutôt que de redéclarer la chaîne à chaque endroit — évite les
 * accents tordus (À vs A) et facilite la recherche dans le code.
 */
export const A_QUALIFIER = 'À qualifier' as const

/**
 * Toutes les criticités, dans l'ordre de gravité. Source unique : filtres,
 * graphiques et validation des préférences en dérivent — même règle que
 * STATUTS_ORDRE, jamais recopier la liste.
 */
export const CRITICITES_ORDRE: readonly Criticite[] = ['Critique', 'Majeur', 'Mineur', A_QUALIFIER]

/** Criticité tranchée : celle qu'un coordinateur choisit (qualification, édition). */
export type CriticiteTranchee = Exclude<Criticite, typeof A_QUALIFIER>

/** Les criticités qu'un coordinateur peut choisir — tout sauf la sentinelle. */
export const CRITICITES_TRANCHEES: readonly CriticiteTranchee[] = ['Critique', 'Majeur', 'Mineur']

/** Helper de type — vérifie qu'une valeur de criticité est encore à qualifier. */
export function estAQualifier(value: string | undefined | null): boolean {
  return value === A_QUALIFIER
}

/**
 * Statut d'un problème.
 *
 * ─── Cycle de vie (Lot 2, retours coordinateurs du 18/09/2026) ───
 *
 *   Ouvert ──qualification──→ En cours ──clôture──→ Clos
 *     │
 *     └──annulation (à la qualification)──→ Annulé
 *
 *  - Le passage à « En cours » est porté par la QUALIFICATION, automatique.
 *    La fonction « Prise en charge » qui le faisait à la main a été retirée.
 *  - « Annulé » = ce n'était pas un problème, c'était déjà réglé, doublon…
 *    Le problème sort des vues actives mais reste consultable et tracé.
 *
 * ⚠️ PRÉ-REQUIS SHAREPOINT : `Annulé` doit exister À L'IDENTIQUE (é accentué)
 *    dans les listes de choix de SCE_Problemes/field_14 ET de
 *    SCE_Commentaires/field_6 + field_7 (statut avant/après de la timeline).
 *
 * ⚠️ Ne jamais tester « actif » par `statut !== 'Clos'` : un problème annulé
 *    passerait pour ouvert. Utiliser `estActif()` / `estTermine()`.
 */
export type Statut = 'Ouvert' | 'En cours' | 'Clos' | 'Annulé'

/**
 * Constante pour la valeur « Annulé » — même précaution que A_QUALIFIER :
 * l'accent est un piège, SharePoint rejette `Annule` sans broncher.
 */
export const ANNULE = 'Annulé' as const

/** Tous les statuts, dans l'ordre du cycle de vie. */
export const STATUTS_ORDRE: readonly Statut[] = ['Ouvert', 'En cours', 'Clos', ANNULE]

/** Le problème attend encore une action : qualification, traitement ou clôture. */
export function estActif(issue: { statut: Statut }): boolean {
  return issue.statut === 'Ouvert' || issue.statut === 'En cours'
}

/**
 * Le problème est sorti du circuit, quelle qu'en soit l'issue. On n'y
 * commente plus, on ne le qualifie plus, on ne le clôt plus.
 */
export function estTermine(issue: { statut: Statut }): boolean {
  return issue.statut === 'Clos' || issue.statut === ANNULE
}

/**
 * Le problème compte-t-il comme un VRAI problème ?
 *
 * Un annulé n'en est pas un par définition (« ce n'est pas un problème »,
 * « doublon ») : il ne doit gonfler ni les compteurs d'équipement, ni les
 * statistiques, ni le tableau des équipements récurrents. Il reste en revanche
 * visible dans les historiques, où son badge dit ce qu'il est.
 */
export function compteCommeProbleme(issue: { statut: Statut }): boolean {
  return issue.statut !== ANNULE
}

// ─── Annulation ───

/**
 * Motif d'annulation — liste fermée, doublée d'un commentaire obligatoire.
 *
 * Le motif sert à compter (« combien de doublons ce trimestre ? »), le
 * commentaire à comprendre. Décision 2 du cadrage Lot 0.
 *
 * ⚠️ PRÉ-REQUIS SHAREPOINT : ces libellés doivent exister À L'IDENTIQUE dans
 *    la colonne Choice MotifAnnulation de SCE_Problemes.
 */
export type MotifAnnulation = 'Pas un problème' | 'Déjà réglé' | 'Doublon' | 'Autre'

export const MOTIFS_ANNULATION: readonly MotifAnnulation[] = [
  'Pas un problème',
  'Déjà réglé',
  'Doublon',
  'Autre',
]

/** Garde de type : une chaîne lue dans SharePoint est-elle un motif connu ? */
export function estMotifAnnulation(valeur: unknown): valeur is MotifAnnulation {
  return typeof valeur === 'string' && (MOTIFS_ANNULATION as readonly string[]).includes(valeur)
}

// ─── Badge de clôture (Lot 3) ───

/**
 * Traitement qualité retenu à la clôture — obligatoire, pour tous les métiers
 * (décision 4 du cadrage : `Autre` absorbe les cas hors circuit qualité, il
 * n'y a donc aucune raison d'exempter un métier).
 *
 * ─── Plusieurs badges par problème (arbitrage du 24/09/2026) ───
 *
 * Un même problème peut suivre plusieurs circuits : tracé en FNC-T ET revu au
 * TQC, par exemple. La colonne SharePoint est donc en choix MULTIPLE, et
 * `Issue.badgesCloture` est un tableau.
 *
 * Seule exception : `Autre`, qui signifie « tous les autres cas » — exclusif
 * par définition. Voir `basculerBadge()` et `erreurBadges()`.
 *
 * Les libellés longs et l'aide contextuelle vivent dans
 * `config/badgesCloture.config.ts` : ici, seulement les valeurs stockées.
 *
 * ⚠️ PRÉ-REQUIS SHAREPOINT : ces libellés doivent exister À L'IDENTIQUE dans
 *    la colonne Choice BadgeCloture de SCE_Problemes. Piège : l'accent de
 *    « Rapport de problème ».
 *
 * ⚠️ Les problèmes clos AVANT le Lot 3 n'ont pas de badge, et n'en recevront
 *    jamais d'office (décision 3) : attribuer après coup un traitement qualité
 *    que personne n'a décidé serait fausser un enregistrement qualité.
 */
export type BadgeCloture = 'FNC-T' | 'NC-S' | 'TQC' | 'Rapport de problème' | 'Autre'

export const BADGES_CLOTURE: readonly BadgeCloture[] = [
  'FNC-T',
  'NC-S',
  'TQC',
  'Rapport de problème',
  'Autre',
]

/** Garde de type : une chaîne lue dans SharePoint est-elle un badge connu ? */
export function estBadgeCloture(valeur: unknown): valeur is BadgeCloture {
  return typeof valeur === 'string' && (BADGES_CLOTURE as readonly string[]).includes(valeur)
}

/** Remet des badges dans l'ordre canonique, sans doublon — stockage et affichage stables. */
export function ordonnerBadges(badges: Iterable<BadgeCloture>): BadgeCloture[] {
  const choisis = new Set(badges)
  return BADGES_CLOTURE.filter((b) => choisis.has(b))
}

/**
 * Coche ou décoche un badge dans une sélection — règle de l'interface.
 *
 * « Autre » veut dire « tous les autres cas » : il ne peut pas cohabiter avec
 * un badge précis. Le cocher remplace la sélection ; cocher un badge précis
 * retire « Autre ». Sans cette règle, « FNC-T + Autre » serait enregistrable
 * et ne voudrait rien dire.
 */
export function basculerBadge(
  courants: readonly BadgeCloture[],
  badge: BadgeCloture,
): BadgeCloture[] {
  if (courants.includes(badge)) return courants.filter((b) => b !== badge)
  if (badge === 'Autre') return ['Autre']
  return ordonnerBadges([...courants.filter((b) => b !== 'Autre'), badge])
}

/**
 * Valide une sélection de badges — règle de l'API, qui ne fait pas confiance
 * à l'interface. Retourne le motif du refus, ou null si la sélection est
 * recevable.
 */
export function erreurBadges(badges: readonly unknown[]): string | null {
  if (badges.length === 0) return 'Au moins un badge de clôture est obligatoire.'
  const inconnu = badges.find((b) => !estBadgeCloture(b))
  if (inconnu !== undefined) return `Badge de clôture inconnu : « ${String(inconnu)} ».`
  if (new Set(badges).size !== badges.length) return 'Un badge est présent deux fois.'
  if (badges.includes('Autre') && badges.length > 1) {
    return '« Autre » ne peut pas être combiné à un autre badge.'
  }
  return null
}

/**
 * Le commentaire de clôture n'est obligatoire que pour « Autre » (02/10) : un
 * badge qualité (FNC-T, NC-S, TQC, Rapport) dit déjà comment le problème a été
 * traité et renvoie à un document ; « Autre » ne dit rien — le commentaire est
 * alors la seule trace du traitement.
 */
export function commentaireClotureObligatoire(badges: readonly BadgeCloture[]): boolean {
  return badges.includes('Autre')
}

// ─── Références qualité (Lot 3 bis) ───

/**
 * Badges qui renvoient à un document tracé ailleurs, et dont le numéro peut
 * donc être saisi : la FNC-T dans l'outil qualité, la NC-S côté AQ.
 */
export const BADGES_AVEC_REFERENCE = ['FNC-T', 'NC-S'] as const
export type BadgeAvecReference = (typeof BADGES_AVEC_REFERENCE)[number]

export function aUneReference(badge: BadgeCloture): badge is BadgeAvecReference {
  return (BADGES_AVEC_REFERENCE as readonly string[]).includes(badge)
}

/**
 * Format des références, donné par l'AQ le 24/09/2026 :
 *   FNC-T_<année>-<numéro>   ex. FNC-T_2026-55
 *   NC-S_<année>-<numéro>    ex. NC-S_2026-72
 *
 * Le numéro repart de 1 chaque année et n'est PAS complété de zéros : un seul
 * chiffre en janvier, jusqu'à trois en fin d'année. `FNC-T_2026-05` n'est donc
 * pas la même graphie que la vraie référence `FNC-T_2026-5` : on normalise
 * plutôt que de laisser passer deux écritures d'un même document — c'est ce
 * qui permettra un jour de le retrouver automatiquement dans le SharePoint
 * qualité.
 *
 * ⚠️ La référence est FACULTATIVE : elle peut ne pas encore exister au moment
 *    de clore, et se renseigner après coup depuis la fiche du problème clos.
 */
const MOTIF_REFERENCE = /^(FNC-T|NC-S)_(\d{4})-([1-9]\d{0,2})$/

/** Compose une référence à partir de ses parties saisies, ou null si incomplète. */
export function composerReference(
  badge: BadgeAvecReference,
  annee: string,
  numero: string,
): string | null {
  const an = annee.trim()
  // « 05 » → « 5 » : le numéro officiel n'a pas de zéro de tête.
  const num = numero.trim().replace(/^0+/, '')
  if (!/^\d{4}$/.test(an) || !/^[1-9]\d{0,2}$/.test(num)) return null
  return `${badge}_${an}-${num}`
}

/** Une chaîne est-elle une référence valide POUR CE BADGE ? */
export function estReferenceValide(badge: BadgeAvecReference, ref: string): boolean {
  const m = MOTIF_REFERENCE.exec(ref)
  return m !== null && m[1] === badge
}

/** Découpe une référence pour pré-remplir la saisie. */
export function decomposerReference(ref: string | undefined): { annee: string; numero: string } | null {
  const m = ref ? MOTIF_REFERENCE.exec(ref) : null
  return m ? { annee: m[2] ?? '', numero: m[3] ?? '' } : null
}

/**
 * Libellé d'affichage d'un badge sur un problème : la référence complète
 * quand elle existe (« FNC-T_2026-55 »), le code sinon (« FNC-T »). Le badge
 * EST alors la référence — un seul élément à lire, en tête de fiche.
 */
export function libelleBadge(
  issue: Pick<Issue, 'refFnct' | 'refNcs'>,
  badge: BadgeCloture,
): string {
  if (badge === 'FNC-T' && issue.refFnct) return issue.refFnct
  if (badge === 'NC-S' && issue.refNcs) return issue.refNcs
  return badge
}

/**
 * Valeur de FILTRE — jamais stockée — désignant les problèmes clos sans
 * badge, c'est-à-dire clos avant le Lot 3. Permet de les retrouver pour les
 * reprendre à la main si besoin (décision 3).
 */
export const SANS_BADGE = 'Non renseigné' as const
export type FiltreBadgeCloture = BadgeCloture | typeof SANS_BADGE

export const FILTRES_BADGE: readonly FiltreBadgeCloture[] = [...BADGES_CLOTURE, SANS_BADGE]

/**
 * Le problème passe-t-il un filtre par badge ? Sémantique « l'un des » : un
 * FNC-T + TQC remonte sur TQC seul. Seuls les CLOS portent un badge, donc
 * `SANS_BADGE` retient les clos sans badge (clos avant le Lot 3), pas tous
 * les problèmes actifs — ce qui rendrait le filtre inutile.
 */
export function correspondAuFiltreBadge(
  issue: Pick<Issue, 'statut' | 'badgesCloture'>,
  voulus: ReadonlySet<FiltreBadgeCloture>,
): boolean {
  if (issue.statut !== 'Clos') return false
  return issue.badgesCloture
    ? issue.badgesCloture.some((b) => voulus.has(b))
    : voulus.has(SANS_BADGE)
}

/**
 * Famille d'équipement : libellé textuel libre.
 *
 * Côté SCE_Equipements : 44 valeurs Sartorius (Hipersep Flowdrive Pilot,
 * Resolute Prochrom 200, etc.) — typées en string libre pour éviter de
 * maintenir un type union à 44 cas.
 */
export type FamilleEquipement = string

export type Phase =
  | 'Consultation'
  | 'Ingénierie'
  | 'Fabrication'
  | 'Test'
  | 'Expédition'

/** Toutes les phases, dans l'ordre du projet. Source unique, jamais recopier. */
export const PHASES_ORDRE: readonly Phase[] = ['Consultation', 'Ingénierie', 'Fabrication', 'Test', 'Expédition']

// ─── Rôle utilisateur (déduit de l'UPN) ───

export type UserRole = 'operateur' | 'coordinateur' | 'fournisseur'

/**
 * Normalise une saisie de N° de plan : un plan par ligne, lignes vides et
 * espaces de bord retirés. Chaîne vide = aucun plan.
 */
export function normaliserNumeroPlan(saisie: string): string {
  return saisie
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n')
}

// ─── Référentiel équipement (SharePoint SCE_Equipements) ───

export interface Equipement {
  numeroSerie: string               // ex: "2601-H" ou "2601-SC" — clé
  of: string                        // ex: "OF-2601-0025"
  pn: string                        // ex: "PN4334"
  familleEquipement: FamilleEquipement // 44 valeurs possibles (modèle Sartorius)
  designation?: string              // ex: "Hipersep Flowdrive 600L"
  fourniture?: string               // input Windchill — sert à enrichir/déduire la famille
  cve?: string                      // numéro de commande regroupant plusieurs OF
  chefProjet?: string  // nom du chef de projet (SCE_Equipements)
  client?: string      // client du projet, tel que Windchill l'écrit (« BACHEM ») — 20/10
}

/**
 * Catégorie technique large d'un équipement, utilisée dans SCE_Problemes (field_9).
 * Déduite du suffixe du numéro de série.
 *
 * ⚠️ Ces libellés doivent exister À L'IDENTIQUE dans la liste de choix
 *    SharePoint de field_9 — sinon toute déclaration sur la catégorie
 *    concernée est rejetée à l'écriture.
 */
export type CategorieEquipement =
  | 'Flowdrive'
  | 'BioSC'
  | 'Colonne'
  | 'Slurry / Packing unit'
  | 'Skid Booster'

/**
 * Table de référence suffixe → catégorie. **Source unique de vérité.**
 *
 *   -H  → Flowdrive : toute la famille des modules de pompage, Hipersep
 *         Flowdrive (haute pression, ~100 bar) COMME Resolute Flowdrive
 *         (basse pression, ~5 bar) et Resolute RCC | MU.
 *         ⚠️ Le suffixe n'encode PAS la pression : un Resolute Flowdrive est
 *            basse pression et porte quand même -H.
 *   -S  → BioSC : Resolute BioSC (Lab / Pilot / M), chromatographie continue
 *         multicolonnes pour molécules vivantes et fragiles
 *   -SC → Colonne : Hipersep Prochrom (haute pression) et Resolute Prochrom
 *         (basse pression)
 *   -B  → Slurry / Packing unit : cuves, pompes et vannes servant à préparer
 *         la résine et à la charger dans les colonnes
 *   -SD → Skid Booster
 *
 * Hors périmètre volontairement : -CO (corps de colonne) et -G (groupe
 * hydraulique) sont des SOUS-ENSEMBLES d'une colonne -SC, pas des machines
 * déclarables en tant que telles.
 *
 * La déclinaison fine (52 modèles) vit dans `config/equipements.catalogue.ts`
 * et alimente field_3, pas field_9.
 */
export const CATEGORIES_PAR_SUFFIXE: Record<string, CategorieEquipement> = {
  H: 'Flowdrive',
  S: 'BioSC',
  SC: 'Colonne',
  B: 'Slurry / Packing unit',
  SD: 'Skid Booster',
}

/**
 * Extrait le suffixe lettres d'un numéro de série.
 *
 * ⚠️ Volontairement basé sur une regex ancrée en début de chaîne et non sur
 *    `endsWith` : les machines reprises en upgrade portent un indice de
 *    révision ("1245-H-06"), qu'un `endsWith('-H')` manquerait.
 *
 *   "4618-H"    → "H"
 *   "4626-SC"   → "SC"
 *   "1245-H-06" → "H"
 */
export function suffixeNumeroSerie(numeroSerie: string): string {
  const m = numeroSerie.trim().toUpperCase().match(/^\d{3,4}-([A-Z]{1,3})/)
  return m?.[1] ?? ''
}

/**
 * Clé de rapprochement d'un identifiant Windchill : N° de série, OF, PN, CVE.
 *
 * ─── Pourquoi cette fonction existe ───
 *
 * Ces quatre identifiants viennent d'un export CSV Windchill, pas d'une saisie
 * contrôlée : leur graphie n'est garantie ni stable ni unique. « PN4334 » et
 * « pn4334 » coexistent dans les exports réels, et l'import normalise les N° de
 * série en MAJUSCULES (`extraireNumerosSerie`) alors que les problèmes déjà
 * déclarés portent la graphie d'avant l'import.
 *
 * Rapprocher deux de ces valeurs par `===` produit donc des faux négatifs
 * SILENCIEUX : pas d'erreur, juste un compteur à zéro ou une liste vide. Le
 * défaut a été corrigé deux fois séparément — le PN des fiches projet le
 * 16/09, puis six autres points d'appel — parce que la règle vivait dans des
 * commentaires au lieu d'être du code. Elle est ici, et nulle part ailleurs.
 *
 * ⚠️ C'est une clé de COMPARAISON, jamais une valeur à afficher ni à écrire
 *    dans SharePoint. Pour l'affichage, garder la graphie d'origine.
 *
 *   cleRapprochement('  4626-SC ') === cleRapprochement('4626-sc')  // true
 */
export function cleRapprochement(valeur: string | undefined | null): string {
  return (valeur ?? '').trim().toLowerCase()
}

// ─── Entité principale : un problème déclaré ───
//
// ⚠️ Le déclarant n'est plus stocké explicitement : on utilise la colonne
// système "Créé par" (Author) que SharePoint remplit automatiquement.
// Ça vaut pour les internes ET les guests B2B.

export interface Issue {
  id: string                        // Custom : "P2026-0042" (calculé après création)
  titre: string
  metier: Metier
  /** Sujet (23/10) : absent pour la plupart des problèmes. Le métier reste celui qui corrige. */
  sujet?: Sujet
  famille: string                   // Filtrée selon métier ou sujet (peut valoir 'À qualifier')
  criticite: Criticite              // Inclut 'À qualifier' tant que pas qualifié
  phase: Phase

  // Référence équipement
  numeroSerie: string
  of: string
  pn: string
  familleEquipement: CategorieEquipement // catégorie large, déduite du suffixe

  description: string
  pieceJointe?: string

  // Déclarant : alimenté depuis SharePoint Author (système)
  declarant: string                 // email Azure AD du déclarant
  declarantNom: string              // displayName affiché

  dateDeclaration: string           // ISO 8601 (= Created côté SharePoint)
  statut: Statut
  coordinateur?: string             // displayName du coordinateur (depuis personOrGroup)
  dateCloture?: string
  /**
   * Commentaire de FIN — obligatoire depuis le Lot 2, pour les deux sorties :
   *   statut 'Clos'   → comment le problème a été traité
   *   statut 'Annulé' → pourquoi il a été écarté
   * Une seule colonne pour les deux : un problème ne sort qu'une fois, et le
   * statut dit laquelle des deux sorties a été prise.
   *
   * ⚠️ Avant le Lot 2, ce champ était saisi par la fiche détail puis jeté par
   *    `updateIssue`, qui ne le mappait sur aucune colonne : il ne survivait
   *    que dans le corps du commentaire de timeline. Les problèmes clos avant
   *    le Lot 2 ont donc ce champ vide — leur texte est dans la timeline.
   */
  commentaireCloture?: string
  /**
   * Badges de clôture, dans l'ordre canonique de BADGES_CLOTURE. Renseigné
   * uniquement quand statut === 'Clos', et seulement depuis le Lot 3.
   * Jamais vide : absent (`undefined`) quand il n'y a pas de badge.
   */
  badgesCloture?: BadgeCloture[]
  /**
   * Références qualité (Lot 3 bis), facultatives : présentes seulement si le
   * badge correspondant est retenu ET que le numéro a été saisi — à la
   * clôture ou après coup. Format complet, ex. « FNC-T_2026-55 ».
   */
  refFnct?: string
  refNcs?: string
  /**
   * N° de plan(s) concerné(s) — MECA uniquement, renseigné APRÈS la
   * déclaration par le coordinateur (à tout moment, clos compris). Un plan
   * par ligne : la colonne SharePoint est un texte multiligne.
   */
  numeroPlan?: string
  /**
   * Schéma électrique (Lot 13) — ELEC / AUTOM, renseigné après coup par le
   * coordinateur : numéro `NVS50312-G`, révision `01`, page(s) `12-14`.
   * Règles et révision la plus récente : utils/documentationEDE.ts.
   */
  numeroEDE?: string
  revEDE?: string
  pageEDE?: string
  /** Renseignés uniquement quand statut === 'Annulé'. */
  motifAnnulation?: MotifAnnulation
  dateAnnulation?: string
  /**
   * Qui suit ce problème (Lot 12, cloche « Suivre ») — suivi MANUEL
   * uniquement : ni le déclarant ni un commentateur ne suivent d'office.
   * Vide quand personne ne suit. Règles : utils/abonnements.ts.
   */
  abonnes: Abonne[]
  /**
   * Multi-équipements (Lot 14, option C) : un même défaut sur plusieurs
   * machines = une fiche par machine, reliées par l'ID de la PREMIÈRE fiche
   * du groupe (« P2026-0042 »), qu'elle porte aussi. Absent pour une fiche
   * seule. Règles : utils/groupes.ts.
   */
  groupe?: string
}

/** Une personne abonnée à un problème (colonne Abonnes, Personne multiple). */
export interface Abonne {
  /** Identifiant SharePoint de la personne (celui qu'écrit ensureUser). */
  lookupId: number
  /** Nom affiché, quand SharePoint le renvoie. */
  nom?: string
  email?: string
}

// ─── Type de commentaire/événement dans le fil d'activité ───

export type CommentaireType =
  | 'Creation'
  | 'Commentaire'
  | 'ChangementStatut'
  | 'Cloture'
  | 'Qualification' // Lot 3.5 — famille + criticité ; depuis le Lot 2, fait aussi passer Ouvert → En cours
  | 'Annulation'    // Lot 2 — motif + commentaire, statut → Annulé
  | 'Modification'  // Lot 3 bis — référence qualité, N° de plan ; Lot 6 — édition par le coordinateur

/**
 * Un événement ou message posté sur un problème (SharePoint SCE_Commentaires).
 *
 * ⚠️ `auteur` est désormais alimenté par la colonne système Author de SharePoint
 * ("Créé par") — plus de colonne Auteur dédiée.
 */
export interface Commentaire {
  id: string                        // Custom : "P2026-0042-C01"
  problemId: string                 // FK vers Issue.id (ex: "P2026-0042")
  type: CommentaireType
  auteur: string                    // displayName depuis Author SharePoint
  date: string                      // ISO 8601 (= Created côté SharePoint)
  contenu: string
  ancienStatut?: Statut
  nouveauStatut?: Statut
  /** Identifiant SharePoint de la ligne : la modification par son auteur (23/10). */
  itemId?: string
  /** Adresse de l'auteur, ramenée à la personne (compte secondaire, appareil partagé). */
  auteurEmail?: string
  /** Corrigé par son auteur après coup (23/10) : date de la correction. */
  modifieLe?: string
}

// ─── Filtres pour la liste des problèmes ───

export interface IssueFilters {
  metier?: Metier[]
  criticite?: Criticite[]
  statut?: Statut[]
  pn?: string
  of?: string
  numeroSerie?: string
  /**
   * Identité(s) du déclarant. Accepte un tableau parce qu'une même personne
   * peut apparaître sous deux identités : un guest B2B est stocké dans Author
   * sous son e-mail externe, jamais sous son UPN `#EXT#`.
   * Toujours alimenter via `identitesDeclarant()` (utils/identiteDeclarant).
   * Filtré côté client — cf. getIssues.
   */
  declarant?: string | string[]
  familleEquipement?: CategorieEquipement
  recherche?: string
  /**
   * Filtre dédié aux problèmes encore "À qualifier" (Famille OU Criticité non
   * renseignée). Utile pour le dashboard coordinateur futur.
   */
  aQualifier?: boolean
  /**
   * Badge de clôture (Lot 3). Ne retient que des problèmes CLOS : un problème
   * actif ou annulé n'a pas de badge et n'en aura jamais. `SANS_BADGE` désigne
   * les clos sans badge, c'est-à-dire clos avant le Lot 3.
   *
   * Sémantique « L'UN DES » : un problème FNC-T + TQC remonte si l'on filtre
   * sur TQC. Filtré côté client — colonne non indexée, cf. getIssues.
   */
  badgeCloture?: FiltreBadgeCloture[]
}

// ─── Payload de création d'un problème ───
//
// ⚠️ Lot 3.5 — `famille` et `criticite` sont retirés du payload :
//    initialisés côté API à 'À qualifier'.

export type NewIssuePayload = Omit<
  Issue,
  | 'id'
  | 'declarant'
  | 'declarantNom'
  | 'dateDeclaration'
  | 'statut'
  | 'dateCloture'
  | 'commentaireCloture'
  | 'badgesCloture'
  | 'refFnct'
  | 'refNcs'
  | 'numeroPlan'
  | 'numeroEDE'
  | 'revEDE'
  | 'pageEDE'
  | 'motifAnnulation'
  | 'dateAnnulation'
  | 'coordinateur'
  | 'abonnes'                       // ← suivi manuel (Lot 12) : personne à la création
  | 'famille'                       // ← retiré : forcé à 'À qualifier' par l'API
  | 'criticite'                     // ← retiré : forcé à 'À qualifier' par l'API
> & {
  // Documentation connue dès la déclaration (15/10), facultative : le N° de
  // plan d'un MECA, ou l'EDE d'un ELEC / AUTOM (déjà au bon format, cf.
  // normaliserSaisieEDE / normaliserNumeroPlan). Jamais écrits s'ils sont vides.
  numeroPlan?: string
  numeroEDE?: string
  revEDE?: string
  pageEDE?: string
}

// ─── Utilisateur courant ───

export interface CurrentUser {
  upn: string
  nom: string
  email: string
  role: UserRole
}
